import Link from 'next/link';

import CardWrapper from '@/components/layout/CardWrapper/CardWrapper';
import R2Image from '@/components/ui/R2Image';
import RichText from '@/components/ui/RichText/RichText';
import SpecialityBadge from '@/components/ui/SpecialityBadge/SpecialityBadge';

import type { Article } from './types';
import ArticleTitle from './ArticleTitle';

const MAX_VISIBLE_TOPICS = 2;

export default function ArticleCard({
  article,
  href,
  showTopics = false,
}: {
  article: Article;
  href: string;
  showTopics?: boolean;
}) {
  const topics = article.topics?.topics ?? [];
  const visibleTopics = topics.slice(0, MAX_VISIBLE_TOPICS);
  const overflowCount = topics.length - MAX_VISIBLE_TOPICS;

  return (
    <CardWrapper
      columns="one-column"
      paddingClassName=""
      className="cursor-pointer overflow-hidden rounded-xl bg-white shadow-sm"
    >
      <Link href={href} className="flex h-full flex-col">
        <div className="relative h-72 w-full shrink-0 overflow-hidden">
          <R2Image
            path={article.imageUrl}
            alt={article.title.content}
            unoptimized
            objectFit="cover"
            className="object-cover"
            sizes="(max-width: 639px) 100vw, (max-width: 975px) 50vw, 33vw"
          />
        </div>

        <div className="flex flex-1 flex-col justify-between gap-2 p-4">
          {showTopics && topics.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {visibleTopics.map((topic) => (
                <SpecialityBadge key={topic} label={topic} />
              ))}
              {overflowCount > 0 && (
                <SpecialityBadge label={`+${overflowCount}`} />
              )}
            </div>
          ) : (
            article.speciality && (
              <div className="self-start">
                <SpecialityBadge label={article.speciality} />
              </div>
            )
          )}

          <div className="flex min-h-20 flex-col">
            <ArticleTitle article={article} level={3} />
          </div>

          <RichText styles={article.description.styles} className="flex-1">
            {article.description.content}
          </RichText>

          {(article.author || article.date) && (
            <div className="mt-4 flex items-center justify-between">
              {article.author && (
                <div className="flex items-center gap-2">
                  {article.iconUrl ? (
                    <R2Image
                      path={article.iconUrl}
                      alt={article.author.content}
                      width={40}
                      height={40}
                      className="shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-10 w-10 shrink-0 rounded-full bg-blue-200" />
                  )}
                  <RichText styles={article.author.styles}>
                    {article.author.content}
                  </RichText>
                </div>
              )}
              {article.date && (
                <RichText
                  styles={article.date.styles}
                  className="flex shrink-0 items-center gap-1"
                >
                  {article.date.content}
                </RichText>
              )}
            </div>
          )}
        </div>
      </Link>
    </CardWrapper>
  );
}
